import { TOGGLE_VIEW } from 'store/userSessionManager/constants'
import { PLAYBOOK_STEP_MAP } from 'utils/adminExperienceHelpers'
import { getK5DigitalFromPreferences } from 'utils/sitePreferenceHelpers'

/** @typedef {{ curriculum?: string, grade?: string, unit_number?: string | number }} UnitSummaryRow */

export const LESSON_COMPLETION_SPECIALIZED_UNITS_LABEL = 'Specialized Units'
export const DIGITAL_WELL_BEING_UNIT_LABEL = 'Digital Well-Being Unit'

export const getAdminLessonCompletionToggleView = ({
  programKey,
  k8ToggleView,
  hsToggleView,
}) => {
  if (programKey === 'DWB') return TOGGLE_VIEW.SPECIALIZED_UNITS
  if (programKey === 'HS') return hsToggleView
  if (programKey === 'K8') return k8ToggleView
  return k8ToggleView
}

/** Standalone DWB admin uses K8 specialized lesson-completion API payloads. */
export const resolveLessonCompletionApiProgram = program =>
  program === 'DWB' ? 'K8' : program

export const isSpecializedLessonCompletionToggleView = toggleView =>
  toggleView === TOGGLE_VIEW.SPECIALIZED_UNITS

export const normalizeSiteId = siteId => Number(siteId)

export const addMissingSiteIdToNullSiteDetailsResponse = (data, siteId) => {
  const responseData = data?.data ?? data
  return responseData?.SiteDetails === null
    ? { ...data, missingSiteId: siteId }
    : data
}

/** Stable cache segment for the current DWB-enabled admin site id set. */
export const getDwbSiteIdsCacheSegment = (dwbSiteIds = []) => {
  if (!Array.isArray(dwbSiteIds) || dwbSiteIds.length === 0) {
    return 'dwb:none'
  }

  const normalized = [
    ...new Set(dwbSiteIds.map(id => String(id)).filter(id => id !== '')),
  ].sort((a, b) => {
    const numA = Number(a)
    const numB = Number(b)
    if (!Number.isNaN(numA) && !Number.isNaN(numB)) return numA - numB
    return a.localeCompare(b)
  })

  return `dwb:${normalized.join(',')}`
}

/** Cache key for admin lesson completion (core vs specialized API payloads). */
export const getLessonCompletionCacheKey = ({
  toggleView,
  k8ToggleView,
  program,
  siteId = null,
  dwbSiteIds = [],
}) => {
  const resolvedToggleView = toggleView ?? k8ToggleView
  const viewKey = isSpecializedLessonCompletionToggleView(resolvedToggleView)
    ? 'specialized'
    : 'core'
  const scopeKey = siteId != null ? String(siteId) : 'multi'
  const dwbKey = getDwbSiteIdsCacheSegment(dwbSiteIds)
  return `${program ?? ''}:${scopeKey}:${viewKey}:${dwbKey}`
}

export const hasLessonCompletionCacheEntry = (cache, cacheKey) =>
  Boolean(cache?.[cacheKey]?.schools && cache?.[cacheKey]?.unitSummary)

/** DWB rows use curriculum DWB, aggregate unit_number "All", and/or dwb* grades. */
export const isDwbUnitSummaryRow = us => {
  if (us.curriculum === 'DWB') return true
  if (us.curriculum === 'Core' || us.curriculum === 'BHU') return false
  if (us.unit_number === 'All') return true
  return /^dwb/i.test(String(us.grade ?? ''))
}

export const filterUnitSummaryForView = (unitSummary, isSpecializedView) =>
  unitSummary.filter(us =>
    isSpecializedView ? isDwbUnitSummaryRow(us) : !isDwbUnitSummaryRow(us),
  )

export const getSchoolwideUnitRow = ({
  filteredUnitSummary,
  isSpecializedView,
  siteId,
}) => {
  const normalizedSiteId = normalizeSiteId(siteId)
  const aggregateUnitNumber = isSpecializedView ? 'All' : 'AllCore'

  const schoolwide = filteredUnitSummary.find(
    us =>
      normalizeSiteId(us.site_id) === normalizedSiteId &&
      us.grade === 'All' &&
      us.unit_number === aggregateUnitNumber,
  )
  if (schoolwide) return schoolwide

  if (!isSpecializedView) return undefined

  return filteredUnitSummary.find(
    us =>
      normalizeSiteId(us.site_id) === normalizedSiteId &&
      us.grade === 'All' &&
      us.unit_number === '1',
  )
}

export const siteHasDwbUnitSummary = (filteredUnitSummary, siteId) =>
  filteredUnitSummary.some(
    us => normalizeSiteId(us.site_id) === normalizeSiteId(siteId),
  )

export const coercePublishFlowBoolean = value => {
  if (value === null || value === undefined) {
    return null
  }

  if (typeof value === 'boolean') {
    return value
  }

  if (value === 'true' || value === 1) {
    return true
  }

  if (value === 'false' || value === 0) {
    return false
  }

  return null
}

export const getPublishFlowCompleteValue = (publishFlow, fieldName) =>
  publishFlow?.publishFlowItems?.find(item => item.name === fieldName)
    ?.completeValue ?? null

export const getCurrentYearPublishFlow = publishFlows =>
  publishFlows?.find(flow => flow.name === PLAYBOOK_STEP_MAP.name) ?? null

export const buildPublishFlowsBySiteId = publishFlows => {
  if (!Array.isArray(publishFlows)) {
    return {}
  }

  return publishFlows.reduce((acc, flow) => {
    const siteId = flow?.siteId
    if (siteId == null) {
      return acc
    }

    const key = normalizeSiteId(siteId)
    if (!acc[key]) {
      acc[key] = []
    }

    acc[key].push(flow)
    return acc
  }, {})
}

export const resolveLessonCompletionGoalIsDisabled = ({
  publishFlows,
  preferences,
}) => {
  const currentPublishFlow = getCurrentYearPublishFlow(publishFlows)
  const publishFlowDisabled = coercePublishFlowBoolean(
    getPublishFlowCompleteValue(
      currentPublishFlow,
      'LessonCompletionGoalIsDisabled',
    ),
  )

  if (publishFlowDisabled !== null) {
    return publishFlowDisabled
  }

  const k5Digital = getK5DigitalFromPreferences(preferences)
  return k5Digital?.lessonCompletionGoalIsDisabled ?? false
}
